import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getfloorDatabyStaus } from "../store/actions/floorActions";
import "./building_page.css";
import { Helmet } from "react-helmet";

const TABLE_HEAD = ["Unit Number", "Gross SQFT", "Terrace SQFT", "Status"];

const buttonLabels = {
  ButtonP: "Parking",
  Button0: "1",
  Button1: 5,
  Button2: 6,
  Button3: 7,
  Button4: 8,
  Button5: 9,
  Button6: 10,
  Button7: 11,
  Button8: 12,
  Button9: 14,
  Button10: 15,
  Button11: 16,
};

export default function Availability() {
  const [selectedButton, setSelectedButton] = useState("Button0");
  const floorData = useSelector((state) => state.floor.list);
  const dispatch = useDispatch();

  const imageButtonClick = (event) => {
    setSelectedButton(event.target.name);
  };

  const floorNum = buttonLabels[selectedButton];

  useEffect(() => {
    dispatch(getfloorDatabyStaus({ floorNum, status: "all" }));
  }, [selectedButton, dispatch]);

  const getButtonClass = (buttonName) => {
    const baseClass =
      "w-1/5 max-w-[281px] min-w-[110px] px-4 py-2 font-semibold text-white border border-gray-dark rounded-[10px] transition-colors duration-300";
    const activeClass = "bg-[#ad885d] text-white border-[#333f48]";
    const inactiveClass =
      "bg-[#333f48] hover:bg-[#ad885d] hover:border-[#7e6140] hover:text-white ";

    return `${baseClass} ${
      selectedButton === buttonName ? activeClass : inactiveClass
    }`;
  };

  // Sort the floor data by unit number
  const sortedFloorData = floorData?.slice().sort((a, b) => {
    // Convert unit numbers to integers for proper numerical sorting
    const unitA = parseInt(a.unitNumber.replace(/\D/g, ""));
    const unitB = parseInt(b.unitNumber.replace(/\D/g, ""));
    return unitA - unitB;
  });

  const getImagePath = (buttonName) => {
    switch (buttonName) {
      case "ButtonP":
        return "img/resources/new_floor_plans/parking.png";
      case "Button0":
        return "img/resources/new_floor_plans/floor1.png";
      default:
        return `img/resources/new_floor_plans/${buttonName}.png`;
    }
  };

  const getMarginTopClass = (buttonName) => {
    if (buttonName === "ButtonP" || buttonName === "Button0") {
      return "-mt-3 md:-mt-10"; // Less margin for parking and first floor
    }
    return "-mt-16 md:-mt-36"; // Original margins for other floors
  };

  return (
    <>
      <Helmet>
        <title>Available Office Spaces | Union Center - Flushing, NY</title>
        <meta
          name="description"
          content="Explore available office spaces at Union Center Flushing. Choose from various unit sizes ranging from 400 to 1600+ sq ft. Modern, sustainable office spaces for sale in prime Downtown Flushing location."
        />
        <meta
          name="keywords"
          content="Union Center office availability, Flushing commercial space for sale, luxury office units, NYC office space, Queens commercial property"
        />
        <link rel="canonical" href="https://www.3716union.com/availability" />
        <meta
          property="og:title"
          content="Available Office Spaces | Union Center Flushing"
        />
        <meta
          property="og:description"
          content="Modern office spaces for sale in Downtown Flushing. Units from 400-1600+ sq ft available."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.3716union.com/availability"
        />
      </Helmet>
      <div className="flex w-full flex-col bg-[#F0EDE8] py-32">
        <div className="w-full text-[#00000] text-base">
          <section className="w-full md:flex">
            <div className="w-full flex bg-[#F0EDE8] -mt-4 md:-mt-0">
              <div className="w-full max-w-[920px] mx-auto md:pt-[60px] pb-4 md:pb-8 pt-10 text-center px-4">
                <p className="text-[#7e6140] text-4xl md:text-5xl font-normal leading-snug">
                  Availability
                </p>
              </div>
            </div>
          </section>
          <section className="flex-col justify-center w-full md:flex ">
            <div className="w-full px-4 max-w-[1000px] grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mx-auto mb-8 md:mb-16">
              {Object.entries(buttonLabels).map(([buttonName, label]) => (
                <button
                  key={buttonName}
                  onClick={imageButtonClick}
                  name={buttonName}
                  className={`${getButtonClass(
                    buttonName
                  )} w-full max-w-[200px] text-sm sm:text-base px-2 py-2 sm:px-4 sm:py-2`}
                >
                  {buttonName === "ButtonP" ? "Parking" : `Floor ${label}`}
                </button>
              ))}
            </div>
            <div className="w-full relative">
              <div className="w-full md:w-[70%] mx-auto max-w-[950px] overflow-hidden relative">
                <p className="absolute top-5 md:top-12 left-1/2 transform -translate-x-1/2 z-10 text-lg md:text-2xl font-bold text-[#ad885d] bg-none px-4 py-2 rounded-md">
                  {selectedButton === "ButtonP"
                    ? "Parking"
                    : `Floor ${buttonLabels[selectedButton]}`}
                </p>
                <img
                  src={getImagePath(selectedButton)}
                  className={`w-full ${getMarginTopClass(
                    selectedButton
                  )} -mb-5`}
                  alt={`Floor plan for ${buttonLabels[selectedButton]}`}
                  style={{
                    clipPath: "inset(20px 0)",
                  }}
                />
              </div>
            </div>
          </section>
          <div className="w-full max-w-[1000px] mx-auto px-[20px] md:px-[50px] pt-[20px] md:pt-[50px] pb-[50px]">
            <div className="max-w-full overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b-2 border-black">
                    {TABLE_HEAD.map((head) => (
                      <th
                        key={head}
                        className="py-4 px-4 text-left font-medium text-black"
                      >
                        {head}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedFloorData &&
                    sortedFloorData.map((item, index) => (
                      <tr key={index} className="border-b border-gray-300">
                        <td className="pt-4 pb-2 px-4">
                          <p className="text-black">{item.unitNumber}</p>
                        </td>
                        <td className="pt-4 pb-2 px-4">
                          <p className="text-black">{item.SQFT}</p>
                        </td>
                        <td className="pt-4 pb-2 px-4">
                          <p className="text-black">
                            {item.terraceSQFT || "0"}
                          </p>
                        </td>
                        <td className="pt-4 pb-2 px-4">
                          <p
                            className={`text-md font-medium ${
                              item.Status === "Available"
                                ? "text-[#10b981]"
                                : "text-[#ef4444]"
                            }`}
                          >
                            {item.Status}
                          </p>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
